// frontend/src/utils/apiConfig.js
import axios from 'axios';

// ✅ CONFIGURACIÓN DINÁMICA DE URLs
const isDevelopment = process.env.REACT_APP_NODE_ENV === 'development';
const isProduction = process.env.REACT_APP_NODE_ENV === 'production';

export const API_BASE_URL = isProduction 
  ? process.env.REACT_APP_API_URL_PRODUCTION
  : process.env.REACT_APP_API_URL_DEVELOPMENT;

console.log(`🌐 [API CONFIG] Ambiente: ${isProduction ? 'PRODUCCIÓN' : 'DESARROLLO'}`);
console.log(`🌐 [API CONFIG] URL Base: ${API_BASE_URL}`);

// ✅ CONFIGURACIÓN BASE DE AXIOS
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json'
  }
});

// ✅ INTERCEPTOR PARA AGREGAR HEADERS DE AUTENTICACIÓN
apiClient.interceptors.request.use(
  (config) => {
    // Obtener datos del usuario desde localStorage
    const token = localStorage.getItem('token');
    const correo = localStorage.getItem('correo');
    const nombres = localStorage.getItem('nombres');
    const rol = localStorage.getItem('rol');
    const sessionId = localStorage.getItem('sessionId') || 'web-session';

    // ⭐ AGREGAR TOKEN SI EXISTE
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Agregar headers de autenticación si existen
    if (correo) {
      config.headers['usuario-correo'] = correo;
      config.headers['correo'] = correo; // Header alternativo por compatibilidad
    }
    
    if (nombres) {
      config.headers['usuario-nombres'] = nombres;
      config.headers['nombres'] = nombres; // Header alternativo por compatibilidad
    }
    
    if (rol) {
      config.headers['usuario-rol'] = rol;
    }
    
    config.headers['session-id'] = sessionId;

    // Log para debugging (solo en desarrollo)
    if (!isProduction) {
      console.log('🔗 [API] Enviando request:', {
        method: config.method?.toUpperCase(),
        url: `${config.baseURL}${config.url}`,
        headers: {
          'Authorization': token ? 'Bearer ***' : 'No token',
          'usuario-correo': config.headers['usuario-correo'],
          'usuario-nombres': config.headers['usuario-nombres'],
          'usuario-rol': config.headers['usuario-rol']
        }
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ [API] Error en interceptor de request:', error);
    return Promise.reject(error);
  }
);

// ✅ INTERCEPTOR PARA MANEJAR RESPUESTAS Y ERRORES
apiClient.interceptors.response.use(
  (response) => {
    // Log para debugging (solo en desarrollo)
    if (!isProduction) {
      console.log('✅ [API] Respuesta exitosa:', {
        status: response.status,
        url: response.config.url,
        method: response.config.method?.toUpperCase()
      });
    }
    
    return response;
  },
  (error) => {
    const { response, config } = error;
    
    console.error('❌ [API] Error en respuesta:', {
      status: response?.status,
      url: config?.url,
      method: config?.method?.toUpperCase(),
      message: response?.data?.mensaje || response?.data?.message || response?.data?.error || error.message
    });

    // ✅ MANEJAR ERRORES DE AUTENTICACIÓN
    if (response?.status === 401) {
      console.warn('🚫 [AUTH] Token expirado o inválido');
      
      const mensaje = response.data?.mensaje || response.data?.message || 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
      
      alert(`🔐 Sesión Expirada\n\n${mensaje}`);
      
      localStorage.clear();
      window.location.href = '/login';
      
      return Promise.reject(new Error('Sesión expirada'));
    }

    // ✅ MANEJAR ERRORES DE PERMISOS
    if (response?.status === 403) {
      console.warn('🚫 [PERMISOS] Acceso denegado');
      
      const mensaje = response.data?.mensaje || response.data?.message || 'No tienes permisos para realizar esta acción.';
      
      alert(`🚫 Acceso Denegado\n\n${mensaje}`);
      
      return Promise.reject(new Error('Permisos insuficientes'));
    }

    // ✅ MANEJAR RATE LIMITING
    if (response?.status === 429) {
      console.warn('🚨 [RATE-LIMIT] Demasiadas solicitudes');
      
      const mensaje = response.data?.mensaje || response.data?.message || 'Has realizado demasiadas solicitudes. Espera un momento e intenta nuevamente.';
      
      alert(`⏳ Límite de Solicitudes\n\n${mensaje}`);
      
      return Promise.reject(new Error('Rate limit excedido'));
    }

    // ✅ MANEJAR OTROS ERRORES
    if (response?.status >= 500) {
      console.error('🔥 [SERVER] Error interno del servidor');
      
      alert('🔥 Error del Servidor\n\nHa ocurrido un error interno. Por favor, intenta más tarde o contacta al soporte técnico.');
    }

    return Promise.reject(error);
  }
);

// ✅ FUNCIONES HELPER PARA ENDPOINTS ESPECÍFICOS

/**
 * Realizar petición GET con manejo de errores
 */
export const apiGet = async (url, config = {}) => {
  try {
    const response = await apiClient.get(url, config);
    return response.data;
  } catch (error) {
    console.error(`❌ [API-GET] Error en ${url}:`, error.message);
    throw error;
  }
};

/**
 * Realizar petición POST con manejo de errores
 */
export const apiPost = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.post(url, data, config);
    return response.data;
  } catch (error) {
    console.error(`❌ [API-POST] Error en ${url}:`, error.message);
    throw error;
  }
};

/**
 * Realizar petición PUT con manejo de errores
 */
export const apiPut = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.put(url, data, config);
    return response.data;
  } catch (error) {
    console.error(`❌ [API-PUT] Error en ${url}:`, error.message);
    throw error;
  }
};

/**
 * Realizar petición DELETE con manejo de errores
 */
export const apiDelete = async (url, config = {}) => {
  try {
    const response = await apiClient.delete(url, config);
    return response.data;
  } catch (error) {
    console.error(`❌ [API-DELETE] Error en ${url}:`, error.message);
    throw error;
  }
};

/**
 * Verificar si el usuario está autenticado
 */
export const estaAutenticado = () => {
  const correo = localStorage.getItem('correo');
  const nombres = localStorage.getItem('nombres');
  
  return !!(correo && nombres);
};

/**
 * Obtener datos del usuario actual
 */
export const obtenerUsuarioActual = () => {
  if (!estaAutenticado()) {
    return null;
  }
  
  return {
    correo: localStorage.getItem('correo'),
    nombres: localStorage.getItem('nombres'),
    rol: localStorage.getItem('rol'),
    permisos: JSON.parse(localStorage.getItem('permisos') || '{}')
  };
};

/**
 * Cerrar sesión
 */
export const cerrarSesion = () => {
  localStorage.clear();
  window.location.href = '/login';
};

// ✅ EXPORTAR CLIENTE AXIOS POR DEFECTO (para usar como "api")
export default apiClient;